import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { cn } from '@/lib/utils';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Lets the guest pick a table on the restaurant's floor plan for the chosen
 * time. Optional: with no pick, the restaurant assigns a table.
 */
export default function TableFloorPicker({ restaurantId, date, time, partySize, selectedTableId, onSelect }) {
  const [floor, setFloor] = useState(null);
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    onSelect(null);
    axios.get(`${API}/reservations/floor/${restaurantId}`, { params: { date, time, party_size: partySize } })
      .then(res => {
        if (cancelled) return;
        setFloor(res.data);
        setPlanId(res.data.floor_plans[0]?.id || null);
      })
      .catch(() => { if (!cancelled) setFloor(null); });
    return () => { cancelled = true; };
  }, [restaurantId, date, time, partySize]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = floor?.floor_plans.find(p => p.id === planId);
  if (!floor || !plan) return null;
  const tables = floor.tables.filter(t => t.floor_plan_id === plan.id);
  if (!tables.length) return null;
  const image = plan.background_image ? (plan.background_image.startsWith('/api/') ? BACKEND + plan.background_image : plan.background_image) : null;

  return (
    <div className="mt-4" data-testid="table-floor-picker">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium">Pick a table (optional)</label>
        {floor.floor_plans.length > 1 && (
          <select className="text-sm border rounded px-2 py-1" value={planId} onChange={(e) => setPlanId(Number(e.target.value))}>
            {floor.floor_plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>
      <div className="relative w-full rounded-lg border bg-muted/30 overflow-hidden"
        style={{ aspectRatio: `${plan.width} / ${plan.height}`, backgroundImage: image ? `url(${image})` : undefined, backgroundSize: '100% 100%' }}>
        {tables.map(t => (
          <button key={t.id} type="button" disabled={!t.free}
            onClick={() => onSelect(selectedTableId === t.id ? null : t.id)}
            style={{ left: `${(t.pos_x / plan.width) * 100}%`, top: `${(t.pos_y / plan.height) * 100}%`,
                     width: `${(t.width / plan.width) * 100}%`, height: `${(t.height / plan.height) * 100}%` }}
            className={cn('absolute flex flex-col items-center justify-center text-[10px] font-semibold border-2 transition',
              t.shape === 'round' ? 'rounded-full' : 'rounded-md',
              selectedTableId === t.id ? 'bg-primary text-primary-foreground border-primary'
                : t.free ? 'bg-white/90 border-emerald-500 text-emerald-800 hover:bg-emerald-50'
                  : 'bg-gray-200/80 border-gray-300 text-gray-400 cursor-not-allowed')}
            data-testid={`pick-table-${t.id}`}>
            <span>{t.table_number}</span><span className="font-normal">{t.capacity}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {selectedTableId ? `Table ${tables.find(t => t.id === selectedTableId)?.table_number} selected` : 'Green tables are free for your party. Skip to let the restaurant choose.'}
      </p>
    </div>
  );
}
